/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {Container, Point, Sprite, Texture, Text} from 'pixi.js';
import {ITrackEntry} from 'pixi-spine';
import {
    CoreDI,
    RichText, setLayer, SpineButton, Spine, SpineButtonState, Tween, Store, inversifyContainer,
} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {Node} from 'posthtml-parser';
import {Line, LineItemType} from '@gongxigames/framework/core/fragments/display/richText/Line';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {$Container, $RichText, $Spine} from '@gongxigames/framework/core/fragments/BuilderY';
import {$BuyFeatureButton} from '@gongxigames/framework/slotV2/modules/buyFeature/$BuyFeatureButton';
import {coreStore} from '@gongxigames/framework/core/globals';
import {$FullScreenFader} from '@gongxigames/framework/slotV2/JSX/FullScreenFader';
import {$GameAlignContainer} from '@gongxigames/framework/slotV2/JSX/GameAlignContainer';
import {$FullScreenFit} from '@gongxigames/framework/slotV2/JSX/FullScreenFit';

export type BuyFeatureButtonsTypeToDisable = 'show' | 'confirm';

const priceOffset = 133;
const defaultDesktop = {x: 204, y: 1336};
const defaultLandscape = {x: 234, y: 1336};
const defaultPortrait = {x: 375, y: 3100};
const duration = 250;

type BuyFeatureViewOptions = {
    onOpened: () => void;
    onYesClicked: () => void;
    onNoClicked: () => void;
    onClosed: () => void;
};

export class BuyFeatureView {
    public static readonly NAME: symbol = Symbol('BuyFeatureView');
    public container!: Container;

    public buttonContainer = new Container();
    protected isStickyShowButton = false;
    protected buttonConfirm!: SpineButton;
    protected buttonShow!: Container;
    protected buttonCancel!: SpineButton;
    protected buttonsSpine!: SpineButton[];
    protected featurePrice?: RichText;
    protected featureText?: RichText;
    protected spineContainer = new Container();
    protected spine!: Spine;
    protected spineName = 'popup_buy_feature';
    protected spineButtonsName = 'buttions_popup_buy_a_feature';
    protected buyFeatureButtonClickSound = 'buttonclick';
    protected buyFeatureBuySound = 'buttonclick';
    protected tween!: Tween;

    protected buttonShowTextAlpha = Store.signal(1);
    protected buttonShowDisabled = Store.signal(true);

    private _gameConfig!: GameConfig;

    protected featureName!: string;
    protected showFader = Store.signal(false);

    constructor(protected options: BuyFeatureViewOptions) {
        this.init();
    }

    public init(): void {
        this.getElements();
        this.createMainScene();
        this.setLayerForElements();
        this.setupButtonsEvents();
        this.positionButton();
    }

    protected createMainScene(): void {
        this.container = <$Container name='buyFeature'>
            <$GameAlignContainer>
                {this.buttonContainer}
            </$GameAlignContainer>
            <$FullScreenFader opacity={0.5} duration={duration} show={this.showFader}/>
            <$FullScreenFit width={1980 * 2} height={1080 * 2}>
                {this.spineContainer}
            </$FullScreenFit>
        </$Container> as Container;
    }

    protected getElements(): void {
        this.featurePrice = <$RichText
            localeID=''
            name='buyFeaturePrice'
            style='BuyFeaturePriceStyle'
            maxWidth={314}
            maxHeight={314}
        /> as RichText;

        if (this.gameConfig.buyFeatureConfig?.featureNameTranslationKey) {
            this.featureName = slotStore.languageService.value!.get(this.gameConfig.buyFeatureConfig?.featureNameTranslationKey);
        }

        this.buttonShow = <$BuyFeatureButton
            name='buyFeatureButton'
            action={this.onShowPopupButtonClicked.bind(this)}
            textAlpha={this.buttonShowTextAlpha}
            disabled={this.buttonShowDisabled}
        /> as Container;

        this.createSpine();
        this.createSlots();
        this.setYesNoButtons();

        this.buttonContainer.addChild(this.buttonShow);

        if (this.featurePrice) {
            this.buttonContainer.addChild(this.featurePrice);
        }
    }

    protected createSpine(): void {
        this.spine = <$Spine
            spine={this.spineName}
        /> as Spine;
        this.spine.position.set(this.gameConfig.mainFrame.width / 2, this.gameConfig.mainFrame.height / 2 - 100);

        this.buttonConfirm = new SpineButton(this.onConfirmButtonPointerUp.bind(this));
        this.buttonConfirm.setUpSpine(
            <$Spine
                spine={this.spineButtonsName}
            /> as Spine,
            '-',
            '-',
            'press_yes_out',
            'idle_yes');

        this.buttonCancel = new SpineButton(this.onCancelButtonPointerUp.bind(this));
        this.buttonCancel.setUpSpine(
            <$Spine
                spine={this.spineButtonsName}
            /> as Spine,
            '-',
            '-',
            'press_no_out',
            'idle_no');

        this.buttonConfirm.name = 'yes';
        this.buttonCancel.name = 'no';
        this.buttonsSpine = [this.buttonConfirm, this.buttonCancel];
    }

    protected createSlots(): void {
        const slotScale = new Point(0.5, -0.5);

        coreStore.spineUtils.value!.addToSlot(
            this.buttonConfirm.spine,
            <$RichText style='BuyFeaturePopupButtonStyle' localeID='buyFeaturePopupYesText' /> as RichText,
            'yes_text_baf',
            slotScale);

        coreStore.spineUtils.value!.addToSlot(
            this.buttonCancel.spine,
            <$RichText style='BuyFeaturePopupButtonStyle' localeID='buyFeaturePopupNoText' /> as RichText,
            'no_text_baf',
            slotScale);

        coreStore.spineUtils.value!.addToSlot(
            this.spine,
            <$RichText style='BuyFeaturePopupButtonStyle' localeID='buyFeaturePopupText' maxWidth={2550} /> as RichText,
            'feature_name',
            slotScale);
    }

    protected getCurrentSprite(name: string): Sprite {
        return this.spine.getChildByName<Sprite>(name) ?? this.spine.skeleton.findSlot(name).currentSprite;
    }

    protected setYesNoButtons(): void {
        const spriteYes = this.getCurrentSprite('buttion_yes_art_baf');
        const spriteNo = this.getCurrentSprite('buttion_no_art_baf');

        spriteYes.texture = Texture.EMPTY;
        spriteYes.removeChildren();
        spriteNo.texture = Texture.EMPTY;
        spriteNo.removeChildren();

        this.buttonConfirm.scale.set(0.5, -0.5);
        this.buttonCancel.scale.set(0.5, -0.5);

        spriteYes.addChild(this.buttonConfirm);
        spriteNo.addChild(this.buttonCancel);
    }

    protected setLayerForElements(): void {
        setLayer(LAYERS.KEYPAD, this.buttonContainer);
    }

    protected positionButton(): void {
        let position = defaultDesktop;
        let positionType: 'desktop' | 'landscape' | 'portrait' = 'desktop';
        let x = 0;
        let y = 0;

        position = slotStore.stage_isLandscape.value ? defaultLandscape : defaultPortrait;
        positionType = slotStore.stage_isLandscape.value ? 'landscape' : 'portrait';

        const configPosition = this.gameConfig.buyFeatureConfig?.button?.[positionType]?.position;

        if (this.isStickyShowButton) {
            x = configPosition?.x ?? 0;
            y = configPosition?.y ?? 0;
        } else {
            x = configPosition?.x ?? position.x;
            y = configPosition?.y ?? position.y;
        }

        this.setShowButtonPosition(x, y);
    }

    protected setShowButtonPosition(x: number, y: number): void {
        this.buttonShow.position.set(x, y);
        this.featurePrice?.position.set(x, y + priceOffset);
    }

    protected setupButtonsEvents(): void {
        this.buttonConfirm.on('pointerout', this.onPointerOut.bind(this));
        this.buttonCancel.on('pointerout', this.onPointerOut.bind(this));

        this.buttonConfirm.on('pointerdown', this.onPointerDown.bind(this));
        this.buttonCancel.on('pointerdown', this.onPointerDown.bind(this));
    }

    private onPointerDown(e: any): void {
        e.currentTarget.spine.setAnimation({
            animationName: `press_${e.currentTarget.name}_in`
        });
    }

    private get isDisableYes(): boolean {
        return this.buttonConfirm.state === SpineButtonState.DISABLED;
    }

    private onPointerOut(): void {
        if (this.buttonsSpine.filter((b) => b.state === SpineButtonState.DOWN).length) {
            if (this.isDisableYes) {
                this.buttonCancel.idle(true);
            } else {
                this.buttonsSpine.forEach((b) => b.idle(true));
            }
        }
    }

    private onCancelButtonPointerUp(): void {
        this.hide();
        this.options.onNoClicked();
        slotStore.soundPlayer.value!.play('buttonclick');
        this.clickedOnSpineButtons('outro_no');
    }

    protected onConfirmButtonPointerUp(): void {
        const animation = this.spine.spineData.animations.filter((a) => a.name === 'outro_yes')[0];
        const animationDuration = animation?.duration ?? 1.4;
        const delayDuration = Math.round(1000 * animationDuration / 2);

        this.hide();
        slotStore.updateManager.value!.addTimer(() => this.options.onYesClicked(), delayDuration, true);
        slotStore.soundPlayer.value!.play(this.buyFeatureBuySound);
        this.clickedOnSpineButtons('outro_yes');
    }

    private clickedOnSpineButtons(animationName = 'outro_yes'): void {
        this.spine.state.data.defaultMix = 0.3;
        this.spine.setAnimation({
            animationName,
            listeners: {
                complete: () => {
                    this.spine.stop();
                    this.spineContainer.removeChildren();
                }
            }
        });
    }

    private clearSpine(spine: Spine): void {
        spine.state.clearListeners();
        spine.state.clearTracks();
        spine.skeleton.setToSetupPose();
    }

    private resetSpines(): void {
        this.clearSpine(this.spine);
        this.spine.visible = true;
        this.spineContainer.addChild(this.spine);
        slotStore.updateManager.value!.add(this.spine);
        this.spine.start();

        this.buttonsSpine.forEach((b) => this.startSpineButton(b));
    }

    private startSpineButton(button: SpineButton): void {
        this.clearSpine(button.spine);
        slotStore.updateManager.value!.add(button.spine);
        button.visible = true;
        button.spine.start();
        button.disable();
    }

    protected onShowPopupButtonClicked(): void {
        this.resetSpines();

        this.enableSpineButtons();
        this.options.onOpened();
        this.showFader.value = true;
        slotStore.soundPlayer.value!.play(this.buyFeatureButtonClickSound);

        this.spine.state.data.defaultMix = 0;

        this.spine.setAnimation({
            animationName: this.animationName,
            listeners: {
                complete: () => {
                    this.spine.setAnimation({animationName: 'idle', loop: true});
                }
            }
        });
    }

    protected get animationName(): string {
        return slotStore.stage_isPortrait.value ? 'intro_portrait' : 'intro_landscape';
    }

    public hide(): void {
        this.options.onClosed();

        this.showFader.value = false;
        const isDisabled = this.isDisableYes;

        this.buttonsSpine.forEach((b) => {
            const spine = b.spine;
            const animationName = isDisabled ? `disable_${b.name}_out` : `press_${b.name}_out`;

            spine.state.data.defaultMix = 0.3;
            b.disable();
            spine.setAnimation({
                animationName,
                listeners: {
                    complete: (entry: ITrackEntry) => {
                        if (entry.animation.name === animationName) {
                            b.visible = false;
                            spine.stop();
                        }
                    }
                }
            });
        });
    }

    public updatePrice(featurePrice: string): void {
        this.featurePrice?.updateText({customText: featurePrice});

        if (this.featureText) {
            this.patchFeatureTextApplyTagsOnce(featurePrice);
        }
        this?.featureText?.updateText?.({values: {featureName: this.featureName, featurePrice}});
    }

    public disableButton(type: BuyFeatureButtonsTypeToDisable): void {
        if (type === 'show') {
            this.buttonShowDisabled.value = true;
            this.setShowButtonTextAlpha(0.5);
        } else {
            this.buttonConfirm.disable();
            this.buttonConfirm.spine.setAnimation({animationName: 'disable_yes_in'});
        }
    }

    public enableButton(type: BuyFeatureButtonsTypeToDisable): void {
        if (type === 'show') {
            this.buttonShowDisabled.value = false;
            this.setShowButtonTextAlpha(1);
        } else {
            this.enableSpineButtons();
        }
    }

    private enableSpineButtons(): void {
        this.buttonsSpine.forEach((b) => {
            b.enable();
            b.idle(true);
        });
    }

    protected setShowButtonTextAlpha(alpha: number): void {
        this.buttonShowTextAlpha.value = alpha;

        if (this.featurePrice) {
            this.featurePrice.alpha = alpha;
        }
    }

    public toggleShowButton(visible: boolean): void {
        if (this.tween) {
            slotStore.updateManager.value!.remove(this.tween);
        }
        this.tween = slotStore.updateManager.value!.addTween(this.buttonContainer).to({alpha: Number(visible)}, duration).start();
        this.buttonContainer.visible = visible;
    }

    public setStickyPosition(value: boolean): void {
        this.isStickyShowButton = value;
        this.positionButton();
    }

    private patchFeatureTextApplyTagsOnce(featurePrice: string): void {
        const regEx = new RegExp(featurePrice, 'ig');
        // @ts-expect-error - applyTags is private
        const featureTextApplyTags = this.featureText.applyTags;

        // @ts-expect-error - applyTags is private
        this.featureText.applyTags = (context: CursorContext, nodeTree: Node[]) => {
            featureTextApplyTags.call(this.featureText, context, nodeTree);
            const lines = context._lines;

            lines.forEach((line: Line) => {
                const {elements} = line;

                elements.forEach((element) => {
                    const {item} = element as {item: LineItemType;};

                    if (!(item instanceof Text)) {
                        return;
                    }
                    const {text} = item;

                    item.text = text.replace(regEx, featurePrice);
                });
            });
            // @ts-expect-error - applyTags is private
            this.featureText.applyTags = featureTextApplyTags;
        };
    }

    protected get gameConfig(): GameConfig {
        this._gameConfig ??= inversifyContainer.get<GameConfig>(CoreDI.GameConfig);

        return this._gameConfig;
    };
}
